<script lang="ts">
    import {ToastMessageService} from 'shared/components/kp-toast-messages';
    import type {FieldTypeId, Fond, RecordSearchParams, UUID} from 'src/typings';
    import {getCurrentLanguage, getInjector, getLocalization, getLogger} from 'src/core';
    import type {ColumnDef, ColumnPinningState, ColumnSizingState, ExpandedState, TableOptions, VisibilityState} from '@tanstack/svelte-table';
    import {createSvelteTable, getCoreRowModel, getExpandedRowModel} from '@tanstack/svelte-table';
    import type {Updater} from '@tanstack/table-core';
    import type {GridField, HierarchicalRecordRow, KpRecordGridDisplayEvents, RecordRow, RowCreationParams, SimpleGridFieldValue, SourceAwareRecordRow} from './lib/types';
    import type {SvelteComponentConstructor} from 'core/types';
    import type {GridContext} from 'shared/ui-widgets/grid/grid.context';
    import {getGridContext, setGridContext} from 'shared/ui-widgets/grid/grid.context';
    import type {FieldTypeDefinition, FondFieldTypeDefinitions, GridLayout} from 'src/features/record/field-types/types';
    import {derived, writable} from 'svelte/store';
    import {defaultCellContent, defaultRowId, getStateOf, replaceTableData, updateTableState} from 'shared/ui-widgets/grid/utils';
    import {createRender} from 'svelte-render';
    import {assertTrue, cleanup, exists, getId, isNullOrUndefined} from 'shared/utils/custom-utils';
    import {RecordGridService} from './services/record-grid.service';
    import {arrayShallowEqual, ascendingOrderComparator, byIdOf, distinctBy, isNotEmpty, replaceAll} from 'shared/utils/array-utils';
    import {createEventDispatcher, onDestroy, onMount} from 'svelte';
    import {getSearchContext} from '../search/kp-search-context/search-context';
    import {combineLatestWith, distinctUntilChanged, distinctUntilKeyChanged, filter, map, tap} from 'rxjs/operators';
    import {resolveRelatedRecordsDescriptors, aggregateSubRowCreateParams, supportsRecordRelations} from './lib/grid-utils';
    import {extractAllSimpleGridFields, getFieldByFieldTypeIdRecursive} from './lib/grid-fields';
    import {createHierarchicalRows, createRootOnlyHierarchicalRows, markRowsWithDataSource,} from './lib/record-row';
    import {asSupported} from './lib/grid-value-editors';
    import {RecordGridDataManager} from './lib/record-grid-data-manager';
    import {createGridSettings} from 'shared/ui-widgets/grid/grid-settings';
    import {BehaviorSubject} from 'rxjs';
    import {SearchManagerBuilderFactoryService} from 'src/features/search/search-manager/search-manager-factory.service';
    import {asUuid} from 'shared/utils/types-utils';
    import {recordGridTableSettingsStorage} from './lib/settings-storage';
    import {isEqual} from 'lodash-es';
    import {COLUMN_TYPES, CONTROL_COLUMN_TYPES} from './lib/constants';
    import KpGenericGrid from 'shared/ui-widgets/grid/KpGenericGrid.svelte';
    import HeaderCell from './components/cells/HeaderCell.svelte';
    import DefaultRowOptionsCell from './components/cells/DefaultRowOptionsCell.svelte';
    import DataCell from './components/cells/data-cell/DataCell.svelte';
    import ControlFooterCell from './components/cells/ControlFooterCell.svelte';
    import TreeViewCell from './components/cells/TreeViewCell.svelte';
    import HoldingsManagementCell from './components/cells/HoldingsManagementCell.svelte';
    import KpSearchToolbar from 'src/features/search/kp-search-toolbar/KpSearchToolbar.svelte';
    import RecordGridPaginationContainer from './components/container/RecordGridPaginationContainer.svelte';
    import GridContextMenuButtons from './components/context/GridContextMenuButtons.svelte';
    import RecordLinkOptionsCell from './components/cells/RecordLinkOptionsCell.svelte';
    import type {Conjunction, Restriction} from 'src/features/search/search-criteria/criteria.types';

    export let fond: Fond;
    export let gridLayout: GridLayout
    export let insertableFonds: Fond[];

    export let referenceRecordId: UUID | null;
    export let rowCreationParams: RowCreationParams = {initialValues: {}};
    export let recordLinkFieldTypeId: FieldTypeId | null;

    export let includeToolbar: boolean;
    export let stickyHeader: boolean;
    export let pinControlColumn: boolean;
    export let expandedFirstLevel: boolean;
    export let includeAddNewRow: boolean;
    export let gridContext: GridContext<HierarchicalRecordRow> | null = null;
    export let rowOptionsCellComponent: SvelteComponentConstructor = DefaultRowOptionsCell;
    export let infiniteScroll: boolean;
    export let draftsPlacement: 'bottom' | 'top' = 'top';

    const logger = getLogger();
    const toastMessageService = getInjector().getByClass(ToastMessageService);
    const searchManagerBuilderFactoryService = getInjector().getByClass(SearchManagerBuilderFactoryService);
    const localize = getLocalization();
    const locale = getCurrentLanguage();
    const recordGridService = getInjector().getByClass(RecordGridService);
    const searchManager = getSearchContext<RecordRow, RecordSearchParams>();
    const dispatch = createEventDispatcher<KpRecordGridDisplayEvents>();

    let isFullscreen = false;

    const handlePopstate = (event: PopStateEvent) => {
        if (!isFullscreen) {
            return;
        }

        event.preventDefault();
        history.pushState(null, '', window.location.href);
        isFullscreen = false;
    };

    let gridCanIncludeRelatedRecords = supportsRecordRelations(gridLayout);
    $: gridCanIncludeRelatedRecords = supportsRecordRelations(gridLayout);

    let {columnPinning, columnSizing, columnVisibility} = recordGridTableSettingsStorage.loadFondSettings(fond);
    $: recordGridTableSettingsStorage.saveFondSettings(fond, {
        columnPinning,
        columnSizing,
        columnVisibility
    });

    const options = createTableOptions(gridLayout, insertableFonds);
    export let table$ = createSvelteTable(options);

    const settings = createGridSettings<HierarchicalRecordRow>({
        columnPinningEnabled: true,
        columnResizingEnabled: true,
        condensed: true,
        mainGroupsDivided: true,
        stickyHeader,
        colorAccented: true,
        selectionEnabled: true,
        editable: true,
        showOnlyTopAndBottomHeaders: true,
        hoverRows: true
    });

    setGridContext<HierarchicalRecordRow>(table$, settings);
    gridContext = getGridContext<HierarchicalRecordRow>();
    const {stateManager, command$} = gridContext;

    const flatRootRows$ = new BehaviorSubject<SourceAwareRecordRow[]>([]);
    const flatDraftRootRows$ = new BehaviorSubject<SourceAwareRecordRow[]>([]);
    const flatSubrows$ = new BehaviorSubject<SourceAwareRecordRow[]>([]);
    const expandedRecordRowsIds = new Set<UUID>();
    const rowExpansionRequestsPerRowCounter = new Map<UUID, number>();
    const rowExpansionRequestsPerRowCounter$ = writable(rowExpansionRequestsPerRowCounter);
    const rowsWithSubrowsLoading$ = derived(rowExpansionRequestsPerRowCounter$, (perRowCounter) => Array.from(perRowCounter.keys()));

    const dataSubscription = flatRootRows$.pipe(
        combineLatestWith(flatSubrows$),
        map(([flat, subrows]) => [...flat, ...subrows]),
        map((rows) => gridCanIncludeRelatedRecords ? createHierarchicalRows(distinctBy(rows, getId()), referenceRecordId) : createRootOnlyHierarchicalRows(rows)),
        filter((rows) => exists(rows)),
        distinctUntilChanged(isEqual),
        combineLatestWith(flatDraftRootRows$.pipe(map((rows) => createRootOnlyHierarchicalRows(rows)))),
        map(([flat, drafts]) => draftsPlacement === 'top' ? [...drafts, ...flat] : [...flat, ...drafts]),
        tap((rows) => replaceTableData(options, rows))
    ).subscribe();

    const searchSubscription = searchManager.getPagination()
        .getPaginationData$()
        .pipe(
            map((data) => data.items),
            distinctUntilChanged(arrayShallowEqual),
            map((rows) => markRowsWithDataSource(rows, 'root', null)))
        .subscribe(flatRootRows$);

    const draftOnlySearchParams = {
        includeDraft: true,
        includeActive: false,
        includeDeleted: false,
        includeExcluded: false
    };
    const draftSearchSubscription = searchManager.createDerivedSearch((params) => ({
        ...params,
        ...draftOnlySearchParams,
        pageNumber: 1
    }), (params) => !exists(params.pageNumber) || params.pageNumber <= 1) // assume there is not more than 1 page of drafts
        .getPagination()
        .getPaginationData$()
        .pipe(
            map((data) => data.items),
            distinctUntilChanged(arrayShallowEqual),
            map((rows) => markRowsWithDataSource(rows, 'draft', null)))
        .subscribe(flatDraftRootRows$);

    const subrowSearchManager = searchManagerBuilderFactoryService.createBuilder<RecordSearchParams, RecordRow>()
        .withStaticParams({...searchManager.getStaticParams(), includeDraft: true})
        .withAllowedSimultaneousRequests()
        .createLocalSearch();
    const subrowSearchSubscription = subrowSearchManager
        .getSearchResult$()
        .pipe(
            tap((result) => decreaseRowExpansionRequestsCounter(result.params)),
            distinctUntilKeyChanged('content', arrayShallowEqual),
            map((result) => markRowsWithDataSource(result.content, 'subrow', asUuid(result.params.parentRowId))))
        .subscribe(replaceOrAppendSubRowRecords);

    const recordGridDataManager = new RecordGridDataManager(
        table$,
        stateManager,
        flatRootRows$,
        flatDraftRootRows$,
        flatSubrows$,
        command$,
        recordGridService,
        rowCreationParams.initialValues,
        localize,
        locale,
        logger,
        toastMessageService,
        dispatch
    );

    const isProcessingEdits$ = recordGridDataManager.getIsProcessingEdits$();

    onMount(() => {
        window.addEventListener('popstate', handlePopstate);
    });

    onDestroy(() => {
        cleanup(dataSubscription, searchSubscription, draftSearchSubscription, subrowSearchSubscription);
        recordGridDataManager.disconnect();
        window.removeEventListener('popstate', handlePopstate);
    });

    function handleBeforeUnload(event: BeforeUnloadEvent) {
        if ($isProcessingEdits$) {
            event.preventDefault();
        }
    }

    function decreaseRowExpansionRequestsCounter(params: RecordSearchParams) {
        assertTrue(gridCanIncludeRelatedRecords, 'Grid should be expandable, if there are expanded rows');
        const recordRowId = asUuid(params.parentRowId);
        const currentRequestCount = rowExpansionRequestsPerRowCounter.get(recordRowId);
        if (currentRequestCount === 1) {
            rowExpansionRequestsPerRowCounter.delete(recordRowId);
        } else {
            rowExpansionRequestsPerRowCounter.set(recordRowId, currentRequestCount - 1);
        }
        rowExpansionRequestsPerRowCounter$.set(rowExpansionRequestsPerRowCounter);
    }

    function replaceOrAppendSubRowRecords(subRowRecords: SourceAwareRecordRow[]) {
        let loadedRecords = [...flatSubrows$.getValue()];
        const loadedRecordsIds = new Set(loadedRecords.map((row) => row.id));
        subRowRecords.forEach((subrow) => {
            if (loadedRecordsIds.has(subrow.id)) {
                loadedRecords = replaceAll(loadedRecords, byIdOf(subrow), subrow);
            } else {
                loadedRecords.push(subrow);
                loadedRecordsIds.add(subrow.id);
            }
        });
        flatSubrows$.next(loadedRecords);
    }

    function createDataColumns(definitions: FondFieldTypeDefinitions): ColumnDef<HierarchicalRecordRow>[] {
        return definitions.fieldTypes.sort(ascendingOrderComparator).map((column) => createDataColumn(column));
    }

    function createDataColumn(definition: FieldTypeDefinition): ColumnDef<HierarchicalRecordRow> {
        if (isNullOrUndefined(definition.fieldTypes) || definition.fieldTypes.length === 0) {
            return {
                id: definition.id,
                meta: {
                    text: definition.text,
                    columnType: COLUMN_TYPES.DATA_COLUMN,
                    editorType: asSupported(definition.editor.defaultValue.type),
                    valueDatatype: definition.valueDatatype,
                    enabled: definition.enabled,
                    editable: definition.editable,
                    required: definition.required,
                    staticDependentFieldTypeIds: definition.staticDependentFieldTypeIds
                },
                accessorFn: (row) => extractAllSimpleGridFields(getFieldByFieldTypeIdRecursive(row, definition.id)),
                header: () => createRender(HeaderCell, {
                    text: definition.text,
                    label: definition.id,
                    fieldTypeId: definition.id
                }),
                cell: (ctx) => {
                    return createRender(DataCell, {
                        values: ctx.cell.getValue<GridField<SimpleGridFieldValue>[]>(),
                        column: ctx.column,
                        fieldType: definition,
                        row: ctx.row,
                        recordGridDataManager
                    });
                }
            };
        }
        return {
            id: definition.id,
            meta: {text: definition.text, columnType: COLUMN_TYPES.TOP_COLUMN},
            header: () => createRender(HeaderCell, {
                text: definition.text,
                label: definition.id,
                fieldTypeId: definition.id
            }),
            columns: definition.fieldTypes.map((subColumn) => createDataColumn(subColumn))
        };
    }

    function createControlColumn(fonds: Fond[]): ColumnDef<HierarchicalRecordRow> {
        const shouldRenderFooter = includeAddNewRow && isNotEmpty(fonds);

        const createOptionsColumn = () => ({
            id: CONTROL_COLUMN_TYPES.OPTIONS,
            meta: {text: '', columnType: COLUMN_TYPES.CONTROL},
            enableHiding: false,
            header: () => createRender(HeaderCell, {text: localize(/* @kp-localization commons.Options */ 'commons.Options')}),
            cell: ({row}) => createRender(rowOptionsCellComponent, {recordGridDataManager, row})
        });

        const createHoldingsManagementColumn = () => ({
            id: CONTROL_COLUMN_TYPES.HOLDINGS,
            meta: {text: '', columnType: COLUMN_TYPES.CONTROL},
            enableHiding: false,
            header: () => createRender(HeaderCell, {text: localize(/* @kp-localization record.RecordUsages */ 'record.RecordUsages')}),
            cell: ({row}) => createRender(HoldingsManagementCell, {recordGridDataManager, row})
        });

        const createTreeViewColumn = () => ({
            id: CONTROL_COLUMN_TYPES.TREE_VIEW,
            meta: {text: '', columnType: COLUMN_TYPES.CONTROL},
            enableHiding: false,
            header: () => createRender(HeaderCell, {text: localize(/* @kp-localization record.Hierarchy */ 'record.Hierarchy')}),
            cell: ({row}) => createRender(TreeViewCell, {
                recordGridDataManager,
                row,
                createParams: aggregateSubRowCreateParams(resolveRelatedRecordsDescriptors(gridLayout, row.original.fond)),
                rowsWithSubrowsLoading$
            })
        }) satisfies ColumnDef<HierarchicalRecordRow>;

        let columns = [createOptionsColumn(), createHoldingsManagementColumn()];
        if (gridCanIncludeRelatedRecords) {
            columns = [...columns, createTreeViewColumn()];
        }

        return {
            id: COLUMN_TYPES.CONTROL,
            meta: {text: '', columnType: COLUMN_TYPES.CONTROL},
            header: () => createRender(HeaderCell, {text: ''}),
            footer: shouldRenderFooter ? () => createRender(ControlFooterCell, {
                recordGridDataManager,
                insertableFonds: fonds
            }) : undefined,
            columns
        };
    }

    function createTableOptions(definitions: FondFieldTypeDefinitions, fonds: Fond[]) {
        const createRecordLinkOptionsCell = (): ColumnDef<HierarchicalRecordRow> => ({
            id: CONTROL_COLUMN_TYPES.RECORD_LINK,
            meta: {text: '', columnType: COLUMN_TYPES.CONTROL},
            enableHiding: false,
            enableResizing: false,
            enablePinning: false,
            header: () => createRender(HeaderCell, {text: ''}),
            cell: ({row}) => createRender(RecordLinkOptionsCell, {row, recordLinkFieldTypeId})
        });

        const optionsData: TableOptions<HierarchicalRecordRow> = {
            data: [],
            columns: [createRecordLinkOptionsCell(), createControlColumn(fonds), ...createDataColumns(definitions)],
            getSubRows: (row) => row.subrows ?? [],
            getCoreRowModel: getCoreRowModel(),
            getExpandedRowModel: getExpandedRowModel(),
            getRowCanExpand: (row) => !expandedRecordRowsIds.has(row.original.id) || (row.original.subrows?.length ?? 0) > 0,
            getRowId: defaultRowId(),
            onExpandedChange: handleOnExpandedChange,
            onColumnPinningChange: handleOnColumnPinningChange,
            onColumnSizingChange: handleOnColumnSizingChange,
            columnResizeDirection: 'ltr',
            onColumnVisibilityChange: handleOnColumnVisibilityChange,
            state: {
                columnPinning,
                columnSizing,
                columnVisibility
            },
            defaultColumn: {
                cell: defaultCellContent(),
                enableResizing: true,
                enablePinning: true,
                enableHiding: true
            },
            manualPagination: true
        };

        if (pinControlColumn && (!exists(columnPinning.left) || !columnPinning.left.includes(COLUMN_TYPES.CONTROL))) {
            columnPinning.left = [...(columnPinning.left ?? []), COLUMN_TYPES.CONTROL];
        }

        return writable<TableOptions<HierarchicalRecordRow>>(optionsData);
    }

    let expandedTableState: ExpandedState = {};

    function handleOnExpandedChange(updater: Updater<ExpandedState>) {
        const currentExpandedRows = new Set(Object.keys(expandedTableState));
        expandedTableState = getStateOf(expandedTableState, updater);
        const updatedExpandedRows = new Set(Object.keys(expandedTableState));
        const rowsToExpand = Array.from(updatedExpandedRows.difference(currentExpandedRows));
        loadSubrowsForRows(rowsToExpand);
        updateTableState(options, {expanded: expandedTableState});
    }

    function loadSubrowsForRows(rowsIdsToExpand: string[]) {
        if (rowsIdsToExpand.length === 0) {
            return;
        }
        assertTrue(gridCanIncludeRelatedRecords, 'Grid should be expandable, if there are expanded rows');

        rowsIdsToExpand.forEach((rowId) => {
            const recordRow = $table$.getRow(rowId, true).original;
            const recordRowId = recordRow.id;
            if (expandedRecordRowsIds.has(recordRowId)) {
                return;
            }
            const relatedRecordsDescriptors = resolveRelatedRecordsDescriptors(gridLayout, recordRow.fond);
            const dynamicFieldParamNames = relatedRecordsDescriptors.map((descriptor) => descriptor.searchParams.dynamicFieldSearchParamName);
            const restrictions: Restriction[] = dynamicFieldParamNames.map((dynamicFieldParamName) => ({field: dynamicFieldParamName, eq: {value: recordRowId}} as unknown as Restriction));
            const disjunctionRestriction = {or: restrictions} as unknown as Restriction;
            const staticParamsQt = subrowSearchManager.getStaticParams().qt;
            const rootQtConjunction: Conjunction = exists(staticParamsQt) ? JSON.parse(staticParamsQt) : {and: []}
            rootQtConjunction.and.push(disjunctionRestriction)
            const inheritedDynamicParamNames = Object.keys(subrowSearchManager.getStaticParams()).filter((paramName) => paramName.startsWith('field_'));
            subrowSearchManager.newSearchWithPartialParams({
                parentRowId: recordRowId,
                qt: JSON.stringify(rootQtConjunction),
                ...Object.fromEntries(inheritedDynamicParamNames.map((dynamicParamName) => [dynamicParamName, undefined])) // delete dynamic field search param
            });
            expandedRecordRowsIds.add(recordRowId);
            const currentRequestCount = rowExpansionRequestsPerRowCounter.get(recordRowId) ?? 0;
            rowExpansionRequestsPerRowCounter.set(recordRowId, currentRequestCount + 1);
            rowExpansionRequestsPerRowCounter$.set(rowExpansionRequestsPerRowCounter);
        });
    }

    function handleOnColumnPinningChange(updater: Updater<ColumnPinningState>) {
        columnPinning = getStateOf(columnPinning, updater);
        updateTableState(options, {columnPinning});
    }

    function handleOnColumnSizingChange(updater: Updater<ColumnSizingState>) {
        columnSizing = getStateOf(columnSizing, updater);
        updateTableState(options, {columnSizing});
    }

    function handleOnColumnVisibilityChange(updater: Updater<VisibilityState>) {
        columnVisibility = getStateOf(columnVisibility, updater);
        updateTableState(options, {columnVisibility});
    }
</script>

<svelte:window on:beforeunload={handleBeforeUnload}/>

<div class="kp-record-grid-container" class:fullscreen={isFullscreen}>
    {#if includeToolbar}
        <div class="kp-record-grid-toolbar">
            <KpSearchToolbar {table$}
                             showLoadingIndicator={$isProcessingEdits$}
                             showFullscreenButton
                             bind:isFullscreen
                             on:fond-change/>
        </div>
    {/if}

    <div class="kp-record-grid-display">
        <RecordGridPaginationContainer pagination={searchManager.getPagination()}
                                       tableState$={stateManager.state$}
                                       fondId={fond.id}
                                       fieldTypeDefinitions={gridLayout}
                                       {infiniteScroll}
                                       {table$}>

            <KpGenericGrid {table$}
                           {expandedFirstLevel}
                           additionalClasses="record-grid"
                           additionalContainerClasses="record-grid-container">

                <svelte:fragment slot="grid-context-menu" let:contextElementRef>
                    <GridContextMenuButtons {contextElementRef}
                                            fieldTypeDefinitions={gridLayout}
                                            {recordGridDataManager}
                                            {table$}/>
                </svelte:fragment>
            </KpGenericGrid>
        </RecordGridPaginationContainer>
    </div>
</div>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";

    .kp-record-grid-container {
        display: flex;
        flex-direction: column;
        gap: @spacing-ml;

        &.fullscreen {
            position: fixed;
            top: @spacing-xl;
            bottom: @spacing-xl;
            left: @spacing-xxl;
            right: @spacing-xxl;
            z-index: @modal-z-index;
            border-radius: @border-radius-large;
            background-color: @themed-body-bg;
            box-shadow: rgba(0, 0, 0, 0.25) 0 54px 55px, rgba(0, 0, 0, 0.12) 0px -12px 30px, rgba(0, 0, 0, 0.12) 0px 4px 6px, rgba(0, 0, 0, 0.17) 0px 12px 13px, rgba(0, 0, 0, 0.09) 0px -3px 5px;
            width: unset !important; // Override width: 100% from ErpPageLayout
        }
    }

    .kp-record-grid-toolbar {
        padding-inline: @spacing-xxl;
        padding-top: @spacing-ml;
    }

    .kp-record-grid-display {
        display: flex;
        flex-direction: column;
    }

    :global {
        .kp-record-grid-display {
            .add-new-record-btn {
                height: 40px;
            }

            .record-grid-container {
                border: 1px solid @themed-border-muted;
            }
        }
    }
</style>
